import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPlans, getCurrentPlan } from '../../../store/Payment/index';
import { formatNumber } from '../../../utils/formatNumber';

/**
 * Self-contained Pricing Plans Display Component
 * Backend returns filtered plans, frontend handles UI toggles for planType and billingPeriod
 */
const PricingPlans = ({ onSelectPlan, showBackButton = false, onBack }) => {
  const dispatch = useDispatch();

  // Redux state
  const { plans, loading } = useSelector((state) => state['Payment']);
  const { currentPlan = {}, isSubWorkspace } = useSelector((state) => state['Payment']['selectedPlan']);
  const { currentSubscription = {} } = useSelector((state) => state['Settings']);

  // Check if subscription needs reactivation (locked or inactive)
  const needsReactivation = currentSubscription['isLocked'] || currentSubscription['status'] === 'inactive' || currentSubscription['status'] === 'past_due';

  // Local state - UI toggles only, backend handles actual filtering
  // Initialize planType and billingPeriod based on current plan
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [planType, setPlanType] = useState(currentPlan?.['planType'] || 'business');
  const [billingPeriod, setBillingPeriod] = useState(currentPlan?.['interval'] || 'month');

  // Plan families and billing intervals actually returned by the backend drive the toggles
  const availablePlanTypes = [...new Set((plans || []).map(plan => plan['planType']))];
  const availableIntervals = [...new Set((plans || []).map(plan => plan['interval']))];
  const hasQuarterlyPlans = availableIntervals.includes('quarter');
  const hasYearlyPlans = availableIntervals.includes('year');
  const hasLifetimePlans = availableIntervals.includes('lifetime');

  // Update planType and billingPeriod when currentPlan changes (e.g., after data loads)
  useEffect(() => {
    if (currentPlan?.['planType']) setPlanType(currentPlan['planType']);
    if (currentPlan?.['interval']) setBillingPeriod(currentPlan['interval']);
  }, [currentPlan?.['planType'], currentPlan?.['interval']]);

  // Keep selections valid for the plan families/intervals the backend actually returned
  useEffect(() => {
    if (availablePlanTypes.length && !availablePlanTypes.includes(planType)) setPlanType(availablePlanTypes[0]);
    if (availableIntervals.length && !availableIntervals.includes(billingPeriod)) setBillingPeriod(availableIntervals[0]);
  }, [plans, planType, billingPeriod]);

  // Helper functions for display
  const getMonthlyPrice = (plan) => INTERVAL_MONTHS[plan['interval']] ? Math.round(plan['price'] / INTERVAL_MONTHS[plan['interval']]) : plan['price'];

  // Savings vs paying month-by-month for the same number of months (3 for quarter, 12 for year)
  const calculateSavings = (monthlyPrice, intervalPrice, months) => {
    if (monthlyPrice <= 0) return 0;
    const costIfMonthly = monthlyPrice * months;
    const savings = costIfMonthly - intervalPrice;
    return Math.round((savings / costIfMonthly) * 100);
  };

  // Finds the matching monthly plan's price for savings math
  const getMonthlyBasePrice = (plan) => (plans || []).find(p => p['displayName'] === plan['displayName'] && p['planType'] === plan['planType'] && p['interval'] === 'month')?.['price'] || 0;

  // Handle plan selection with loading state
  const handleSelectPlan = (plan) => {
    if (!onSelectPlan) return;
    setLoadingPlanId(plan['_id']);
    const mode = plan['interval'] === 'lifetime' ? 'payment' : 'subscription';
    onSelectPlan({
      mode,
      planId: plan['_id'],
      priceId: plan['stripePriceId'],
      successCallback: () => setLoadingPlanId(null),
      errorCallback: () => setLoadingPlanId(null)
    });
  };

  // Check if backend returned any plans at all
  const hasPlansFromBackend = plans && plans.length > 0;

  // Simple client-side filtering based on UI toggles
  // Backend already filtered which plans to send (workspace's own plan family only)
  const filteredPlans = (plans || []).filter(plan =>
    plan['planType'] === planType && plan['interval'] === billingPeriod
  );

  // If backend didn't return any plans, show the contact support message
  if (!hasPlansFromBackend) {
    return (
      <div className='pricing-block-area'>
        {showBackButton && onBack && (
          <div className='text-left back-area'>
            <button className='btn-style-border' onClick={onBack}>
              <span className='fa fa-arrow-left mr-2'></span>Back
            </button>
          </div>
        )}
        <div className='row' style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className='col-12 col-md-8 col-lg-6'>
            <div style={{ padding: '40px 30px', textAlign: 'center' }}>
              <div style={{ marginBottom: '20px' }}>
                <i className='fa fa-info-circle' style={{ fontSize: '64px', color: '#6c757d', opacity: '0.8' }}></i>
              </div>
              <h3 style={{ marginBottom: '15px', color: '#212529', fontWeight: '600' }}>No Plans Available</h3>
              <p style={{ color: '#6c757d', fontSize: '16px', lineHeight: '1.6', marginBottom: '0' }}>
                If you are not seeing plans available, please contact customer support for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='pricing-block-area'>
      {showBackButton && onBack && (
        <div className='text-left back-area'>
          <button className='btn-style-border' onClick={onBack}>
            <span className='fa fa-arrow-left mr-2'></span>Back
          </button>
        </div>
      )}

      {!isSubWorkspace && availablePlanTypes.length > 1 && (
        <div className='main-tab-agency-buttons'>
          <div className='btn-group' role='group'>
            {availablePlanTypes.map(type => (
              <button key={type} className={`tab-btn ${planType === type ? 'active' : ''}`} onClick={() => setPlanType(type)}>
                {PLAN_TYPE_LABELS[type] || `${type} Plans`}
              </button>
            ))}
          </div>
        </div>
      )}

      {availableIntervals.length > 1 && (
      <div className='billing-period-toggle'>
        <button className={`tab-btn ${billingPeriod === 'month' ? 'active' : ''}`} onClick={() => setBillingPeriod('month')}>
          Monthly
        </button>
        {hasQuarterlyPlans && (
          <button className={`tab-btn ${billingPeriod === 'quarter' ? 'active' : ''}`} onClick={() => setBillingPeriod('quarter')}>
            Quarterly
            <span className="save-text">Save 20%</span>
          </button>
        )}
        {hasYearlyPlans && (
          <button className={`tab-btn ${billingPeriod === 'year' ? 'active' : ''}`} onClick={() => setBillingPeriod('year')}>
            Yearly
            <span className="save-text">Save 30%</span>
          </button>
        )}
        {hasLifetimePlans && (
          <button className={`tab-btn ${billingPeriod === 'lifetime' ? 'active' : ''}`} onClick={() => setBillingPeriod('lifetime')}>
            Lifetime
            <i className='icon'><svg width="800px" height="800px" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" className="iconify iconify--emojione"><path fill="#f7b600" d="M2 61l8.6-3l-6.5-3z"></path><path fill="#ffdd7d" d="M26.9 36.4L14.8 24.2l-2 5.6z"></path><path fill="#f7b600" d="M12.8 29.8l-2.2 6.3l26.8 12.5l1.3-.4l-11.8-11.8z"></path><path fill="#ffdd7d" d="M8.5 42.4l20 9.3l8.9-3.1l-26.8-12.5z"></path><path fill="#f7b600" d="M6.3 48.7l13.2 6.2l9-3.2l-20-9.3z"></path><path fill="#ffdd7d" d="M6.3 48.7L4.1 55l6.5 3l8.9-3.1z"></path><path d="M31.9 31.2c6.7 6.6 10.2 14 7.8 16.4c-2.5 2.4-9.9-1-16.7-7.7c-6.7-6.6-10.2-14-7.8-16.4c2.5-2.4 9.9 1.1 16.7 7.7" fill="#493816"></path><path d="M23.5 14.5c-1.6-2.3.1-3.3 2.3-2.9c-2.1-2.5-.8-4.3 2.5-3.6c1 .2-.4 1.9-1.3 1.9c2.7 2 1.2 4.2-1.7 3.7c2.6 3.5-1.8 2.6-3.8 2.8C21 19 24 22 23 22c-2.2 0-5.8-8.3.5-7.5" fill="#42ade2"></path><path d="M44.5 19.3c-1.5.7-5.7-5.9-.5-6c-3-2.7-2.6-4 1.4-4.1c-4.6-4.6 2.7-6.2 3.4-3.8c.2.7-2.2-.6-3 .7c-.9 1.5 5.6 5.4-1.1 5.1c2.5 2.5 2.6 3.7-1.3 4.1c.5.8 2.1 3.6 1.1 4" fill="#ff8736"></path><path d="M46.2 34.9l1.5-1.3s1.4 2.1 2.4 2.9c.8-3.6.6-5.7 4.7-3.3c-2.3-6.2 1.5-3.9 5.2-2.2c-.2-1.6 0-1.4 1.6-1.9c1.4 5.3-2.4 3.7-5.4 2c1.8 4.8-.1 4.5-3.9 2.9c-.1 2-.7 4.3-1.9 4.5c-1.4.4-4.2-3.6-4.2-3.6" fill="#ed4c5c"></path><path d="M35 20.1c-1.8 2.4-4.7 3.7-6.8 5.8c-2.2 2.2-3.5 8.2-3.5 8.2s.8-6.3 2.9-8.7c1.9-2.2 4.7-3.8 6.2-6.3c2.6-4.6.2-10.6-3.2-14.1c.7-.6 1.7-1.4 2.2-2c3.3 4.1 6.1 12 2.2 17.1" fill="#c28fef"></path><path d="M38.1 25.2c-2.6 1.9-4.5 4.7-6.3 7.3c-1.6 2.3-6.7 5.2-6.7 5.2s4.8-3.3 6.3-5.7c1.8-3 3.6-6.1 6.4-8.3c5.6-4.3 13.7-3.9 20-1.6c-.4.9-1.1 2.8-1.1 2.8s-13.3-3.6-18.6.3" fill="#ff8736"></path><g fill="#42ade2"><path d="M49.2 24.7c-1.7 2.2-2.5 4.9-3.8 7.4c-1.2 2.3-2.8 4.5-5.1 5.7c-2.6 1.3-8.3.9-8.3.9s5.7-.1 8.1-1.7c2.4-1.6 3.7-4.4 4.6-7c1.8-5 4-10.4 9.2-12.6c.3.9 1 2.8 1 2.8s-2.9.8-5.7 4.5"></path><path d="M3.21 14.325l2.828-2.829l2.829 2.828l-2.828 2.83z"></path></g><path fill="#ff8736" d="M7.173 23.197L10 20.369l2.828 2.828L10 26.025z"></path><path fill="#ed4c5c" d="M14.358 9.822l2.828-2.828l2.828 2.828l-2.828 2.828z"></path><path fill="#c28fef" d="M45.205 43.696l2.828-2.829l2.828 2.829l-2.828 2.828z"></path><path fill="#ed4c5c" d="M38.903 53.39l2.828-2.828l2.829 2.829l-2.829 2.828z"></path><path fill="#ff8736" d="M51.279 55.607l2.828-2.829l2.828 2.829l-2.828 2.828z"></path><g fill="#42ade2"><path d="M54.078 42.731l2.828-2.828l2.828 2.828l-2.828 2.829z"></path><path d="M49.356 12.823l2.828-2.829l2.829 2.829l-2.828 2.828z"></path></g><path fill="#ed4c5c" d="M19.044 29.792l2.829-2.828l2.828 2.828l-2.828 2.829z"></path></svg></i>
          </button>
        )}
      </div>
      )}

      <div className='plans-scroll-container' style={{ maxHeight: billingPeriod === 'lifetime' ? '65vh' : 'none', overflowY: billingPeriod === 'lifetime' ? 'auto' : 'visible', paddingRight: billingPeriod === 'lifetime' ? '10px' : '0' }}>
        <div className='row'>
          {filteredPlans.map(plan => {
            const isCurrentPlan = currentPlan['name'] === plan['name'];
            const showReactivate = isCurrentPlan && needsReactivation && plan['stripePriceId'];
            return (
            <div key={plan['_id'] || plan['name']} className='col-lg-3'>
              <div className={`pricing-block ${isCurrentPlan ? 'active' : ''} ${showReactivate ? 'reactivate-needed' : ''}`}>
                {showReactivate && <span className='pricing-block-reactivate-badge'>Reactivation Required</span>}
                <div className='header-area'>
                  <h5>{plan['displayName']}</h5>
                  <h2>
                    {plan['interval'] === 'lifetime' ?
                      <>${formatNumber(plan['price'])} <span className='text-muted' style={{ fontSize: '14px' }}>one-time</span></>
                    : INTERVAL_MONTHS[plan['interval']] ? (
                      <>
                        ${formatNumber(getMonthlyPrice(plan))} <span>/month</span>
                        <small className='d-block text-muted'>
                          ${formatNumber(plan['price'])}/{plan['interval']}
                          {plan['price'] > 0 && <span className='ml-1'>(Save {calculateSavings(getMonthlyBasePrice(plan), plan['price'], INTERVAL_MONTHS[plan['interval']])}%)</span>}
                        </small>
                      </>
                    ) : <>${formatNumber(plan['price'])} <span>/{plan['interval']}</span></>}
                  </h2>
                  <p>{plan['textBottomPrice'] || plan['description']}</p>
                  {plan['interval'] === 'lifetime' && plan['textBottomPrice2'] && <p className=''>{plan['textBottomPrice2']}</p>}
                  {plan['interval'] === 'lifetime' && plan['oneTimeText'] && <p className=''>{plan['oneTimeText']}</p>}

                  <div className='btn-box text-center'>
                    {isCurrentPlan ? (
                      showReactivate ? (
                        <button className='btn-style-new' onClick={() => handleSelectPlan(plan)} disabled={loadingPlanId === plan['_id']}>
                          {loadingPlanId === plan['_id'] ? 'Loading...' : 'Reactivate Plan'}
                        </button>
                      ) : (
                        <button className='btn-style-border' disabled>Selected Plan</button>
                      )
                    ) : plan['stripePriceId'] ? (
                      <button className='btn-style-border' onClick={() => handleSelectPlan(plan)} disabled={loadingPlanId === plan['_id']}>
                        {loadingPlanId === plan['_id'] ? 'Loading...' : plan['btnText'] || 'Get Started'}
                      </button>
                    ) : (
                      <button className='btn-style-border'>{plan['btnText'] || 'Contact Sales'}</button>
                    )}
                  </div>
                </div>

                <div className='plan-text-area'>
                  <ul>
                    {plan['featuresWithIcons'] && plan['featuresWithIcons'].map((feature, idx) => (
                      <li key={idx}>
                        {feature['iconSvg'] ?
                          <span className='icon' dangerouslySetInnerHTML={{ __html: feature['iconSvg'] }} />
                        : <i className='icon fa fa-check'></i>}
                        {feature['text']}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Display labels for plan families
const PLAN_TYPE_LABELS = { business: 'Business Plans', agency: 'Agency Plans', doneForYou: 'Done For You Plans' };

// Months of service per multi-month interval — drives per-month price and savings math
const INTERVAL_MONTHS = { quarter: 3, year: 12 };

export default PricingPlans;
